import React, { forwardRef } from 'react';
import { Form, Icon, Tooltip } from 'antd';
import moment from 'moment';
import UploadField from '../UploadField/UploadField';
import BaseInput from './components/BaseInput';
import BaseDatePicker from './components/BaseDatePicker';
import BaseSelect from './components/BaseSelect';
import NumberInput from './components/NumberInput';
import useFlatMeterTypes from '../../../queries/useFlatMeterTypes';
import useFlatMeterStatuses from '../../../queries/useFlatMeterStatuses';
import useFlatMeterLocations from '../../../queries/useFlatMeterLocations';
import useFlatMeterTariffs from '../../../queries/useFlatMeterTariffs';
import { fileSizeValidator } from '../../../utils/fileSizeValidator';
import {
  baseQueryOptions,
  fieldNames,
  METER_TEXT_FIELD_LIMIT,
  TOOLTIP_TITLE,
} from './constants';
import { formValuesChange, getRequiredRule, getTextValidator } from './utils';
import {
  defaultFlatMeterTariffCount,
  flatMeterNoDataId,
  REMOVED_METER_ID,
} from '../../../constants/common';
import UploadFiles from './components/UploadFiles';
import { meterTextPattern } from '../../../constants/patterns';
import styles from './MeterForm.module.scss';

const textValidatorRule = {
  validator: getTextValidator({
    maxLength: METER_TEXT_FIELD_LIMIT,
    pattern: meterTextPattern,
  }),
};

const getMomentDate = (date) => (date ? moment(date) : null);
const getTooltipProps = (disabled) => ({
  title: disabled ? TOOLTIP_TITLE : '',
  placement: 'bottom',
});

const TypeSelect = ({ form, disabled, initialValue }) => {
  const { data, isFetching } = useFlatMeterTypes(baseQueryOptions);

  return (
    <BaseSelect
      disabled={disabled}
      form={form}
      items={data}
      loading={isFetching}
      label="Тип"
      name={fieldNames.typeId}
      rules={[getRequiredRule('Укажите тип счетчика', 'number')]}
      initialValue={initialValue}
    />
  );
};

const SerialNumberInput = ({ form, disabled, initialValue }) => {
  return (
    <BaseInput
      disabled={disabled}
      name={fieldNames.serialNumber}
      label="Заводской номер"
      form={form}
      initialValue={initialValue}
      rules={[getRequiredRule('Укажите заводской номер'), textValidatorRule]}
    />
  );
};

const ModelInput = ({ form, disabled, initialValue }) => {
  return (
    <BaseInput
      disabled={disabled}
      name={fieldNames.model}
      label="Модель"
      form={form}
      initialValue={initialValue}
      rules={[textValidatorRule]}
    />
  );
};

const MarkInput = ({ form, disabled, initialValue }) => {
  return (
    <BaseInput
      disabled={disabled}
      name={fieldNames.mark}
      label="Марка"
      form={form}
      initialValue={initialValue}
      rules={[textValidatorRule]}
    />
  );
};

const SealNumberInput = ({ form, disabled, initialValue }) => {
  return (
    <BaseInput
      disabled={disabled}
      name={fieldNames.sealNumber}
      label="Номер пломбы"
      form={form}
      initialValue={initialValue}
      rules={[textValidatorRule]}
    />
  );
};

const StatusSelect = ({ form, initialValue }) => {
  const { data, isFetching } = useFlatMeterStatuses(baseQueryOptions);

  return (
    <BaseSelect
      form={form}
      items={data}
      loading={isFetching}
      label="Статус"
      name={fieldNames.statusId}
      initialValue={initialValue}
    />
  );
};

const TariffCountSelect = ({ form, disabled, initialValue }) => {
  const { data, isFetching } = useFlatMeterTariffs(baseQueryOptions);

  return (
    <BaseSelect
      disabled={disabled}
      form={form}
      items={data}
      loading={isFetching}
      label="Тариф"
      name={fieldNames.tariffCount}
      initialValue={initialValue}
    />
  );
};

const InstallDateField = ({ form, disabled, initialValue }) => {
  return (
    <BaseDatePicker
      disabled={disabled}
      form={form}
      initialValue={initialValue}
      label="Дата установки"
      name={fieldNames.installDate}
      tooltipProps={getTooltipProps(disabled)}
    />
  );
};

const CommissioningDateField = ({ form, disabled, initialValue }) => {
  return (
    <BaseDatePicker
      disabled={disabled}
      form={form}
      initialValue={initialValue}
      label="Дата ввода в эксплуатацию"
      name={fieldNames.commissioningDate}
      tooltipProps={getTooltipProps(disabled)}
    />
  );
};

const VerificationDateField = ({ form, disabled, initialValue }) => {
  return (
    <BaseDatePicker
      disabled={disabled}
      form={form}
      initialValue={initialValue}
      label="Дата поверки"
      name={fieldNames.verificationDate}
      tooltipProps={getTooltipProps(disabled)}
    />
  );
};

const VerificationIntervalInput = ({ form, disabled, initialValue }) => {
  return (
    <BaseInput
      disabled={disabled}
      name={fieldNames.verificationInterval}
      label="Интервал поверки (мес.)"
      form={form}
      initialValue={initialValue}
      renderInput={() => <NumberInput disabled={disabled} />}
    />
  );
};

const NextVerificationDateField = ({ form, disabled, initialValue }) => {
  return (
    <BaseDatePicker
      disabled={disabled}
      form={form}
      initialValue={initialValue}
      label="Планируемая поверка"
      name={fieldNames.nextVerificationDate}
      tooltipProps={{
        title: 'Рассчитывается автоматически: дата поверки + интервал поверки',
        placement: 'bottom',
      }}
      datePickerProps={{
        disabled: true,
        placeholder: '',
        className: styles.disabled,
      }}
    />
  );
};

const LocationSelect = ({ form, disabled, initialValue }) => {
  const { data, isFetching } = useFlatMeterLocations(baseQueryOptions);

  return (
    <BaseSelect
      disabled={disabled}
      form={form}
      items={data}
      loading={isFetching}
      label="Место установки"
      name={fieldNames.locationId}
      initialValue={initialValue}
    />
  );
};

const VisibilityOwnerSelect = ({ visible, hasShowToClient }) => {
  return (
    <div style={{ marginTop: 3 }}>
      <div>Видимость собственнику</div>
      <div style={{ marginTop: 5, marginLeft: 3 }}>
        {!hasShowToClient ? (
          <div>-</div>
        ) : (
          <Tooltip
            title={
              visible
                ? 'Собственник видит счётчик'
                : 'Собственник не видит счётчик'
            }
          >
            <Icon
              type={visible ? 'eye' : 'eye-invisible'}
              style={{ fontSize: 20, marginTop: 5, marginLeft: 3 }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const MeterForm = Form.create({
  name: 'individual_meter_form',
  onValuesChange: formValuesChange,
})(
  forwardRef(({ form, meter, act }, ref) => {
    ref.current = form;

    const disabled = meter?.statusId === REMOVED_METER_ID;

    const showVisibilityOwner = Boolean(meter);

    return (
      <Form className={styles.root}>
        <div className={styles.grid}>
          <TypeSelect
            form={form}
            disabled={disabled}
            initialValue={meter?.type?.id}
          />

          <SerialNumberInput
            form={form}
            disabled={disabled}
            initialValue={meter?.serialNumber}
          />

          <ModelInput
            form={form}
            disabled={disabled}
            initialValue={meter?.model}
          />

          <MarkInput
            form={form}
            disabled={disabled}
            initialValue={meter?.mark}
          />

          <SealNumberInput
            form={form}
            disabled={disabled}
            initialValue={meter?.sealNumber}
          />

          <StatusSelect
            form={form}
            initialValue={meter?.statusId || flatMeterNoDataId}
          />

          <TariffCountSelect
            form={form}
            disabled={disabled}
            initialValue={meter?.tariffCount || defaultFlatMeterTariffCount}
          />

          <InstallDateField
            form={form}
            disabled={disabled}
            initialValue={getMomentDate(meter?.installDate)}
          />

          <CommissioningDateField
            form={form}
            disabled={disabled}
            initialValue={getMomentDate(meter?.commissioningDate)}
          />

          <VerificationDateField
            form={form}
            disabled={disabled}
            initialValue={getMomentDate(meter?.verificationDate)}
          />

          <VerificationIntervalInput
            form={form}
            disabled={disabled}
            initialValue={meter?.verificationInterval}
          />

          <NextVerificationDateField
            form={form}
            disabled={disabled}
            initialValue={getMomentDate(meter?.nextVerificationDate)}
          />

          <LocationSelect
            form={form}
            disabled={disabled}
            initialValue={meter?.location?.id}
          />
          {showVisibilityOwner && (
            <VisibilityOwnerSelect
              hasShowToClient={Boolean(meter?.hasOwnProperty('showToClient'))}
              visible={meter?.showToClient}
            />
          )}
        </div>

        <UploadField
          disabled={disabled}
          form={form}
          label="Акт ввода в эксплуатацию"
          name={fieldNames.actFile}
          rules={[{ validator: fileSizeValidator }]}
          className={styles.formItem}
          uploadProps={{
            style: { marginTop: '5px' },
            showUploadList: { showRemoveIcon: !act },
          }}
          initialValue={
            act ? [{ uid: -1, name: act.name, originFileObj: act }] : []
          }
        />

        {!meter && (
          <UploadFiles
            form={form}
            disabled={disabled}
          />
        )}
      </Form>
    );
  })
);

export default MeterForm;
